import { useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, Pencil, X, Star } from "lucide-react";
import { api, formatINR } from "@/lib/quotationApi";

function MasterItemEditForm({ item, kind, priceKey, priceLabel, onCancel, onSave }) {
  const [name, setName] = useState(item.name);
  const [price, setPrice] = useState(String(item[priceKey]));

  const submit = () => {
    onSave({ name: name.trim(), [priceKey]: parseFloat(price) });
  };

  return (
    <div className="space-y-2">
      <input
        className="input-luxe !h-11"
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-testid={`edit-${kind}-name-${item.id}`}
      />
      <div>
        <label className="label-luxe">{priceLabel}</label>
        <input
          type="number"
          className="input-luxe !h-11"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          inputMode="numeric"
          data-testid={`edit-${kind}-price-${item.id}`}
        />
      </div>
      <div className="flex gap-2 pt-1">
        <button className="btn-ghost !h-10 flex-1" onClick={onCancel}>
          <X size={14} className="inline" /> Cancel
        </button>
        <button
          className="btn-primary !h-10 flex-1"
          onClick={submit}
          data-testid={`save-edit-${kind}-${item.id}`}
        >
          <Save size={14} className="inline mr-1" /> Save
        </button>
      </div>
    </div>
  );
}

export default function MasterItemRow({
  item,
  kind,
  endpoint,
  priceKey,
  priceLabel,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
  onReload,
}) {
  const onToggleQuickAdd = async () => {
    try {
      await api.put(`${endpoint}/${item.id}`, { is_quick_add: !item.is_quick_add });
      toast.success(item.is_quick_add ? "Unpinned from Quick Add" : "Pinned to Quick Add");
      onReload();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div
      className="p-3 border-b border-white/5 last:border-0"
      data-testid={`${kind}-row-${item.id}`}
    >
      {isEditing ? (
        <MasterItemEditForm
          item={item}
          kind={kind}
          priceKey={priceKey}
          priceLabel={priceLabel}
          onCancel={onCancelEdit}
          onSave={onSave}
        />
      ) : (
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="font-medium">{item.name}</div>
            <div className="text-xs text-[#C5A880]">
              {formatINR(item[priceKey])}{" "}
              <span className="text-white/40">/ unit</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onToggleQuickAdd}
              className={`w-9 h-9 rounded-lg hover:bg-white/5 ${
                item.is_quick_add ? "text-[#C5A880]" : "text-white/30 hover:text-[#C5A880]"
              }`}
              data-testid={`quickadd-toggle-${item.id}`}
              aria-label={item.is_quick_add ? "Unpin from Quick Add" : "Pin to Quick Add"}
              title={item.is_quick_add ? "Unpin from Quick Add" : "Pin to Quick Add in Step 3"}
            >
              <Star size={15} fill={item.is_quick_add ? "currentColor" : "none"} />
            </button>
            <button
              onClick={onStartEdit}
              className="w-9 h-9 rounded-lg text-white/60 hover:text-[#C5A880] hover:bg-white/5"
              data-testid={`edit-${kind}-${item.id}`}
              aria-label="Edit"
            >
              <Pencil size={15} />
            </button>
            <button
              onClick={onDelete}
              className="w-9 h-9 rounded-lg text-white/40 hover:text-red-400 hover:bg-white/5"
              data-testid={`delete-${kind}-${item.id}`}
              aria-label="Delete"
            >
              <Trash2 size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
